import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Briefcase, Lock, MessageCircle, MessagesSquare } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { api, useAuth } from '../context/AuthContext';
import { Spinner } from '../components/RatenShared';
import { Thread } from '../components/Thread';

const sortChats = (items) => [...items].sort((a, b) => (b.unread || 0) - (a.unread || 0) || (a.is_general ? -1 : 0) - (b.is_general ? -1 : 0));

const ChatTab = ({ c, active, onClick, t }) => (
  <button type="button" onClick={onClick} data-testid={`nachrichten-chat-${c.id}`}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm whitespace-nowrap transition-colors ${active ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : 'bg-white text-[#1B3A5C] border-gray-200 hover:border-[#b8893d]'}`}>
    {c.is_general ? <MessageCircle className="w-4 h-4" /> : <Briefcase className="w-4 h-4" />}
    <span>{c.is_general ? t('nachrichten.general') : `${c.rechtsgebiet || t('nachrichten.general')}${c.aktenzeichen ? ` · ${c.aktenzeichen}` : ''}`}</span>
    {c.unread > 0 && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${active ? 'bg-[#b8893d] text-white' : 'bg-[#b8893d] text-white'}`} data-testid={`nachrichten-unread-${c.id}`}>{c.unread}</span>}
  </button>
);

const MeineNachrichten = () => {
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [chats, setChats] = useState(null);
  const [active, setActive] = useState(caseId || null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/cases');
      const items = sortChats(data.items || []);
      setChats(items);
      setActive((cur) => (cur && items.some((c) => c.id === cur) ? cur : items[0]?.id || null));
    } catch { setChats([]); }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login?next=/meine-akten'); return; }
    if (['admin', 'kanzlei_mitarbeiter', 'freelancer'].includes(user.role)) { navigate('/kanzlei/eingang', { replace: true }); return; }
    load();
  }, [authLoading, user, navigate, load]);

  useEffect(() => { if (!chats) return; const id = setInterval(load, 20000); return () => clearInterval(id); }, [chats, load]);
  useEffect(() => { if (active && active !== caseId) navigate(`/meine-akten/${active}`, { replace: true }); }, [active, caseId, navigate]);

  const current = chats?.find((c) => c.id === active);
  const unreadTotal = (chats || []).reduce((s, c) => s + (c.unread || 0), 0);

  return (
    <div className="min-h-screen bg-[#f5f8fa] flex flex-col">
      <Seo title={t('nachrichten.title')} description={t('nachrichten.subtitle')} path="/meine-akten" noindex />
      <Header />
      <main className="flex-1 pb-24">
        <section className="bg-[#1B3A5C] text-white">
          <div className="container mx-auto px-4 py-8 sm:py-10">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] text-[#b8893d] uppercase mb-2"><MessagesSquare className="w-4 h-4" /> {t('nachrichten.general')}</div>
            <h1 className="font-serif font-light text-3xl sm:text-4xl tracking-tight" data-testid="nachrichten-title">{t('nachrichten.title')}</h1>
            <p className="text-white/75 text-sm sm:text-base mt-2 max-w-2xl">{t('nachrichten.subtitle')}</p>
            {unreadTotal > 0 && <div className="inline-flex items-center gap-2 mt-4 text-xs bg-[#b8893d] text-white px-3 py-1.5 rounded-full" data-testid="nachrichten-unread-total">{unreadTotal === 1 ? t('nachrichten.unread_one') : t('nachrichten.unread_many', { n: unreadTotal })}</div>}
          </div>
        </section>
        <section className="container mx-auto px-4 py-6 sm:py-8 max-w-4xl">
          {chats === null ? (
            <div className="flex items-center gap-3 text-sm text-[#34536b] py-12 justify-center" data-testid="nachrichten-loading"><Spinner /> {t('nachrichten.loading')}</div>
          ) : chats.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-sm p-10 text-center" data-testid="nachrichten-empty">
              <Lock className="w-8 h-8 text-[#b8893d] mx-auto mb-3" />
              <h2 className="font-serif text-xl text-[#1B3A5C] mb-2">{t('nachrichten.empty_title')}</h2>
              <p className="text-sm text-[#34536b]">{t('nachrichten.empty_text')}</p>
            </div>
          ) : (
            <>
              {chats.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-3 mb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="nachrichten-chat-tabs">
                  {chats.map((c) => <ChatTab key={c.id} c={c} active={c.id === active} onClick={() => setActive(c.id)} t={t} />)}
                </div>
              )}
              {current && (
                <div className="bg-white border border-gray-200 rounded-sm shadow-sm" data-testid="nachrichten-thread">
                  <div className="px-4 sm:px-6 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#1B3A5C] truncate">{current.is_general ? t('nachrichten.general') : current.rechtsgebiet}</div>
                      <div className="text-[11px] text-[#7a8a99] font-mono">{current.aktenzeichen || current.mandanten_nr || ''}</div>
                    </div>
                    {!current.chat_enabled && <span className="text-[10px] tracking-wider uppercase text-rose-700 bg-rose-50 px-2 py-1 rounded-full inline-flex items-center gap-1"><Lock className="w-3 h-3" /> {t('nachrichten.locked')}</span>}
                  </div>
                  {!current.is_general && current.sachstand && (
                    <div className="px-4 sm:px-6 py-3 bg-[#fbf7f0] border-b border-gray-100" data-testid="nachrichten-sachstand">
                      <p className="text-[10px] tracking-[0.2em] uppercase text-[#b8893d]">{t('nachrichten.sachstand')}{current.sachstand_am ? ` · ${new Date(current.sachstand_am).toLocaleDateString(i18n.language || 'de-DE')}` : ''}</p>
                      <p className="text-sm text-[#1B3A5C] mt-1 whitespace-pre-line">{current.sachstand}</p>
                      {current.gegner && <p className="text-[11px] text-[#7a8a99] mt-1">{current.bezeichnung}</p>}
                    </div>
                  )}
                  <Thread key={current.id} caseId={current.id} currentUser={user} isTeam={false} chatEnabled={current.chat_enabled !== false} chatRef={current.aktenzeichen || `M-${current.mandanten_nr || current.id}`} />
                </div>
              )}
              <p className="text-[11px] text-[#7a8a99] mt-4">{t('nachrichten.hint')}</p>
            </>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MeineNachrichten;
